import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { GridComponent, ColumnsDirective, ColumnDirective, Page, Selection, Inject, Toolbar, Sort } from '@syncfusion/ej2-react-grids';
import { Autocomplete, TextField, useMediaQuery } from '@mui/material';

// Mapeo de las opciones del cotizador web (filmsolar.cl) a láminas de bodega.
// El cliente ve "Silver Plata" en la web; la cotización usa la lámina asignada aquí.

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const formatCLP = (value) =>
  value === null || value === undefined
    ? '—'
    : new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(Number(value));

const formatFecha = (iso) =>
  iso ? new Date(iso).toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

// Mismos umbrales que el indicador de Stock
const getStockColor = (stock) => {
  if (stock === 0) return 'gray';
  if (stock <= 5) return 'red';
  if (stock <= 15) return 'orange';
  return 'green';
};

const CodigoChip = ({ codigo }) => (
  <span className="font-mono text-xs bg-gray-100 text-gray-700 rounded px-1.5 py-0.5">{codigo}</span>
);

const laminaTemplate = (props) =>
  props.lamina_codigo ? (
    <div className="flex items-center gap-2 min-w-0">
      <CodigoChip codigo={props.lamina_codigo} />
      <span className="truncate">{props.lamina_nombre}</span>
    </div>
  ) : (
    <span className="text-red-600 font-medium">Sin asignar</span>
  );

const precioTemplate = (props) => formatCLP(props.lamina_preciom2);

const stockTemplate = (props) => {
  if (props.lamina_stock_m2 === null || props.lamina_stock_m2 === undefined) return '—';
  const stock = Number(props.lamina_stock_m2);
  return (
    <div className="flex items-center gap-2">
      <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: getStockColor(stock) }} />
      <span>{stock.toLocaleString('es-CL')} m²</span>
    </div>
  );
};

const activoTemplate = (props) =>
  props.activo ? (
    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 whitespace-nowrap">Activa</span>
  ) : (
    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 whitespace-nowrap">Inactiva</span>
  );

// Definidas fuera del componente: si se recrean en cada render (p. ej. al ocultar el menú lateral),
// el grid de Syncfusion reconstruye las columnas y pierde las plantillas JSX.
const COLUMNAS_DESKTOP = [
  { field: 'nombre_web', headerText: 'Opción en la web', width: '160' },
  { field: 'categoria_display', headerText: 'Categoría', width: '110' },
  { field: 'lamina_nombre', headerText: 'Lámina asignada (código · nombre)', width: '280', template: laminaTemplate },
  { field: 'lamina_preciom2', headerText: 'Precio m²', width: '110', textAlign: 'Right', template: precioTemplate },
  { field: 'lamina_stock_m2', headerText: 'Stock bodega', width: '125', template: stockTemplate },
  { field: 'activo', headerText: 'Estado', width: '95', template: activoTemplate },
];

// Vista móvil: una tarjeta por opción
const TarjetaOpcion = ({ opcion, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full text-left bg-white border border-gray-200 rounded-xl p-4 shadow-sm active:bg-gray-50 hover:border-[#FF743A] transition-colors"
  >
    <div className="flex justify-between items-start gap-3">
      <div className="min-w-0">
        <p className="font-semibold text-gray-900">{opcion.nombre_web}</p>
        <p className="text-xs text-gray-500">{opcion.categoria_display}</p>
      </div>
      {activoTemplate(opcion)}
    </div>
    <div className="mt-3 text-sm text-gray-800">{laminaTemplate(opcion)}</div>
    <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between items-center text-sm">
      <span className="font-medium text-gray-900">
        {formatCLP(opcion.lamina_preciom2)}
        <span className="text-gray-500 font-normal">/m²</span>
      </span>
      <span className="text-gray-600">{stockTemplate(opcion)}</span>
    </div>
  </button>
);

const CatalogoWeb = () => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const gridRef = useRef(null);
  const [opciones, setOpciones] = useState([]);
  const [laminas, setLaminas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busqueda, setBusqueda] = useState('');

  const [seleccionada, setSeleccionada] = useState(null);
  const [laminaElegida, setLaminaElegida] = useState(null);
  const [activo, setActivo] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [modalError, setModalError] = useState(null);

  const fetchDatos = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [resOpciones, resLaminas] = await Promise.all([
        axios.get(`${BACKEND}/api/productos/catalogo-web/`, { headers: authHeaders() }),
        axios.get(`${BACKEND}/api/productos/laminas/`, { params: { tipo_stock: 'bodega' }, headers: authHeaders() }),
      ]);
      setOpciones(resOpciones.data);
      setLaminas([...resLaminas.data].sort((a, b) => a.nombre.localeCompare(b.nombre)));
    } catch (err) {
      console.error('Error al cargar el catálogo web:', err);
      setError(err.response?.status === 403
        ? 'Solo los administradores pueden ver el catálogo web.'
        : 'Error al cargar el catálogo web.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDatos();
  }, []);

  // Filtro de la vista móvil (el grid de escritorio usa su propio buscador)
  const opcionesFiltradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return opciones;
    return opciones.filter((o) =>
      [o.nombre_web, o.categoria_display, o.lamina_codigo, o.lamina_nombre]
        .some((v) => v && v.toLowerCase().includes(q))
    );
  }, [opciones, busqueda]);

  const abrirEdicion = (opcion) => {
    setSeleccionada(opcion);
    setLaminaElegida(laminas.find((l) => l.id === opcion.lamina) || null);
    setActivo(opcion.activo);
    setModalError(null);
  };

  const cerrarEdicion = () => {
    setSeleccionada(null);
    // Permite volver a abrir la misma fila con un nuevo clic
    gridRef.current?.clearSelection();
  };

  const guardar = async () => {
    if (!laminaElegida) {
      setModalError('Selecciona una lámina de bodega.');
      return;
    }
    try {
      setGuardando(true);
      setModalError(null);
      await axios.patch(
        `${BACKEND}/api/productos/catalogo-web/${seleccionada.id}/`,
        { lamina: laminaElegida.id, activo },
        { headers: authHeaders() }
      );
      cerrarEdicion();
      await fetchDatos();
    } catch (err) {
      console.error('Error al guardar el mapeo:', err);
      const data = err.response?.data;
      setModalError(data?.lamina?.[0] || data?.detail || 'No se pudo guardar el cambio.');
    } finally {
      setGuardando(false);
    }
  };

  const precioActual = seleccionada?.lamina_preciom2 !== null && seleccionada?.lamina_preciom2 !== undefined
    ? Number(seleccionada.lamina_preciom2)
    : null;
  const precioNuevo = laminaElegida ? Number(laminaElegida.preciom2) : null;
  const diferencia = precioActual !== null && precioNuevo !== null ? precioNuevo - precioActual : null;

  return (
    <div className="mt-4">
      <p className="text-sm text-gray-600 mb-4 max-w-3xl">
        Define con qué lámina de bodega se cotiza cada opción del cotizador de filmsolar.cl. El cliente ve el nombre
        de la web; la cotización muestra el nombre y precio de la lámina asignada.
        {isMobile ? ' Toca una opción para editarla.' : ' Haz clic en una fila para editarla.'}
      </p>

      {error && <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">{error}</div>}
      {isLoading && <p className="text-sm text-gray-500 mb-2">Cargando…</p>}

      {!error && isMobile && (
        <>
          <input
            type="search"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar opción, código o lámina"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none focus:border-[#FF743A]"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {opcionesFiltradas.map((o) => (
              <TarjetaOpcion key={o.id} opcion={o} onClick={() => abrirEdicion(o)} />
            ))}
          </div>
          {!isLoading && opcionesFiltradas.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-6">Sin opciones que coincidan.</p>
          )}
        </>
      )}

      {!error && !isMobile && (
        <GridComponent
          ref={gridRef}
          dataSource={opciones}
          allowPaging
          pageSettings={{ pageSize: 12 }}
          selectionSettings={{ type: 'Single' }}
          toolbar={['Search']}
          allowSorting
          rowSelected={(args) => abrirEdicion(args.data)}
        >
          <ColumnsDirective>
            {COLUMNAS_DESKTOP.map((col, index) => (
              <ColumnDirective key={index} {...col} />
            ))}
          </ColumnsDirective>
          <Inject services={[Page, Selection, Toolbar, Sort]} />
        </GridComponent>
      )}

      {seleccionada && (
        // En móvil se abre como panel inferior; en escritorio, centrado
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white w-full sm:max-w-xl sm:mx-4 rounded-t-2xl sm:rounded-2xl shadow-lg p-5 sm:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start gap-4 mb-5">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wide text-gray-500">Opción web · {seleccionada.categoria_display}</p>
                <h2 className="text-lg sm:text-xl font-semibold text-gray-800">{seleccionada.nombre_web}</h2>
                <p className="text-xs text-gray-500 mt-1">
                  Última modificación: {formatFecha(seleccionada.actualizado_en)}
                  {seleccionada.actualizado_por_nombre && ` · ${seleccionada.actualizado_por_nombre}`}
                </p>
              </div>
              <button onClick={cerrarEdicion} className="text-gray-400 hover:text-gray-600 text-3xl leading-none" aria-label="Cerrar">&times;</button>
            </div>

            <label className="block text-sm font-medium text-gray-700 mb-1">Lámina de bodega</label>
            <Autocomplete
              options={laminas}
              value={laminaElegida}
              onChange={(e, nueva) => setLaminaElegida(nueva)}
              isOptionEqualToValue={(op, val) => op.id === val.id}
              getOptionLabel={(l) => `${l.codigo} · ${l.nombre}`}
              renderOption={(props, l) => (
                <li {...props} key={l.id}>
                  <div className="flex flex-col sm:flex-row sm:justify-between w-full gap-1 sm:gap-4">
                    <div className="flex items-center gap-2 min-w-0">
                      <CodigoChip codigo={l.codigo} />
                      <span className="truncate">{l.nombre}</span>
                    </div>
                    <div className="text-xs text-gray-500 whitespace-nowrap">
                      {formatCLP(l.preciom2)} · {Number(l.stock_m2).toLocaleString('es-CL')} m²
                    </div>
                  </div>
                </li>
              )}
              renderInput={(params) => <TextField {...params} size="small" placeholder="Busca por código o nombre" />}
              noOptionsText="Sin láminas de bodega que coincidan"
            />
            <p className="text-xs text-gray-500 mt-1">Solo se listan láminas de bodega.</p>

            <div className="mt-5 p-4 rounded-lg bg-gray-50 text-sm space-y-2">
              <div className="flex justify-between gap-4">
                <span className="text-gray-600">Precio actual en la web</span>
                <span className="font-medium whitespace-nowrap">{formatCLP(precioActual)}/m²</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-gray-600">Precio con el cambio</span>
                <span className="font-medium text-right">
                  {formatCLP(precioNuevo)}/m²
                  {diferencia !== null && diferencia !== 0 && (
                    <span className={`block sm:inline sm:ml-2 text-xs sm:text-sm ${diferencia > 0 ? 'text-orange-600' : 'text-green-700'}`}>
                      ({diferencia > 0 ? '+' : '−'}{formatCLP(Math.abs(diferencia))})
                    </span>
                  )}
                </span>
              </div>
              {laminaElegida && Number(laminaElegida.stock_m2) === 0 && (
                <p className="text-orange-600 pt-1">Esta lámina no tiene stock en bodega.</p>
              )}
            </div>

            <label className="flex items-center gap-2 mt-5 text-sm text-gray-700">
              <input type="checkbox" checked={activo} onChange={(e) => setActivo(e.target.checked)} className="w-4 h-4" />
              Opción activa en el cotizador web
            </label>
            {!activo && (
              <p className="text-xs text-gray-500 mt-1">
                Si está inactiva, el cotizador le pedirá al cliente escribir por WhatsApp en vez de mostrar un precio.
              </p>
            )}

            {modalError && <div className="mt-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">{modalError}</div>}

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-6">
              <button
                onClick={cerrarEdicion}
                className="w-full sm:w-auto px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={guardar}
                disabled={guardando}
                className="w-full sm:w-auto bg-[#FF743A] text-white py-2 px-4 rounded-md disabled:opacity-60"
              >
                {guardando ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CatalogoWeb;
